import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import CustomTextField from "../../components/common/Textfield";
import CustomSelect from "../../components/common/Select";

interface UserFormData {
  name: string;
  username: string; // Added username field
  email: string;
  phone: string;
  role: "Admin" | "Operations" | "Sales" | "Support" | "Dev" | "Finance";
  password: string;
  passwordMethod: "manual" | "generate";
  modules: {
    bookings: boolean;
    streams: boolean;
    crew: boolean;
    gear: boolean;
    contacts: boolean;
    memoryTable: boolean;
    venues: boolean;
    sales: boolean;
  };
  status: boolean;
  uploadedFile: File | null;
}

interface AddUserFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (userData: UserFormData) => void;
}

export default function AddUserForm({
  open,
  onClose,
  onSave,
}: AddUserFormProps) {
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    username: "", // Initialize username
    email: "",
    phone: "",
    role: "Operations",
    password: "",
    passwordMethod: "manual",
    modules: {
      bookings: true,
      streams: false,
      crew: false,
      gear: false,
      contacts: false,
      memoryTable: false,
      venues: true,
      sales: false,
    },
    status: true,
    uploadedFile: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Sync username with email whenever email changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      username: prev.email, // Set username equal to email
    }));
  }, [formData.email]);

  // Handle role change and update modules accordingly
  useEffect(() => {
    if (formData.role === "Admin") {
      setFormData((prev) => ({
        ...prev,
        modules: {
          bookings: true,
          streams: true,
          crew: true,
          gear: true,
          contacts: true,
          memoryTable: true,
          venues: true,
          sales: true,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        modules: {
          bookings: true,
          streams: false,
          crew: false,
          gear: false,
          contacts: false,
          memoryTable: false,
          venues: true,
          sales: false,
        },
      }));
    }
  }, [formData.role]);

  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData((prev) => ({
      ...prev,
      password: password,
    }));
  };

  const handleInputChange =
    (field: keyof UserFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      role: event.target.value as UserFormData["role"],
    }));
  };

  const handlePasswordMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      passwordMethod: event.target.value as "manual" | "generate",
      password: "", // Clear password when switching methods
    }));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      password: event.target.value,
    }));
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked,
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      uploadedFile: file,
    }));
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      name: "",
      username: "",
      email: "",
      phone: "",
      role: "Operations",
      password: "",
      passwordMethod: "manual",
      modules: {
        bookings: true,
        streams: false,
        crew: false,
        gear: false,
        contacts: false,
        memoryTable: false,
        venues: true,
        sales: false,
      },
      status: true,
      uploadedFile: null,
    });
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" component="div">
            Add New User
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {/* Basic Information Section */}
          <Box mb={3}>
            <Typography variant="h6" sx={{ fontSize: 16, mb: 2 }}>
              Basic Information
            </Typography>

            {/* Upload Section */}
            <Box
              mb={3}
              alignItems={"center"}
              display="flex"
              justifyContent="center"
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed",
                  borderColor: "grey.300",
                  backgroundColor: "grey.50",
                  cursor: "pointer",
                  borderRadius: "50%",
                  width: 150,
                  height: 150,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  "&:hover": {
                    backgroundColor: "grey.100",
                  },
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="file-upload">
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                    sx={{
                      pointerEvents: "none",
                    }}
                  >
                    <CloudUploadIcon color="action" fontSize="large" />
                    <Typography variant="body2" color="text.secondary">
                      {formData.uploadedFile
                        ? `Selected: ${formData.uploadedFile.name}`
                        : "Click to upload picture"}
                    </Typography>
                  </Box>
                </label>
              </Paper>
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  size="small"
                  value={formData.name}
                  onChange={handleInputChange("name")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Username"
                  variant="outlined"
                  size="small"
                  value={formData.username}
                  disabled // Disable to prevent manual editing
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  size="small"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  size="small"
                  value={formData.phone}
                  onChange={handleInputChange("phone")}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Role Section */}
          <Box mb={3}>
            <FormControl fullWidth>
              <CustomSelect
                labelId="role-select-label"
                value={formData.role}
                label="Role"
                onChange={handleRoleChange as any}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Operations">Operations</MenuItem>
                <MenuItem value="Sales">Sales</MenuItem>
                <MenuItem value="Support">Support</MenuItem>
                <MenuItem value="Dev">Dev</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
              </CustomSelect>
            </FormControl>
          </Box>

          {/* Password Section */}
          <Box mb={3}>
            <Typography variant="h6" sx={{ fontSize: 16, mb: 2 }}>
              Password
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <RadioGroup
                row
                value={formData.passwordMethod}
                onChange={handlePasswordMethodChange}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio size="small" />}
                  label="Manual Entry"
                />
                <FormControlLabel
                  value="generate"
                  control={<Radio size="small" />}
                  label="Generate Password"
                />
              </RadioGroup>
            </FormControl>

            {formData.passwordMethod === "generate" && (
              <Box mb={2}>
                <Button
                  variant="outlined"
                  onClick={generatePassword}
                  fullWidth
                  size="small"
                >
                  Generate Password
                </Button>
              </Box>
            )}

            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              size="small"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handlePasswordChange}
              InputProps={{
                readOnly: formData.passwordMethod === "generate",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor:
                  formData.passwordMethod === "generate"
                    ? "grey.50"
                    : "transparent",
              }}
            />
          </Box>

          {/* Status Section */}
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" mb={1}>
              Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={handleStatusChange}
                  color="primary"
                />
              }
              label={formData.status ? "Active" : "Inactive"}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: "primary.main",
              textTransform: "none",
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}